import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Loader from "../common/Loader";
import { fetchEscalationMatrix } from "../../redux/actions/grievanceActions1";
import {
  StatCard,
  ContentCard,
  DashboardHeader,
  Icons,
} from "../common/DashboardComponents";

const EscalationMetrics = () => {
  const dispatch = useDispatch();
  const { escalationMatrix, loading } = useSelector((state) => ({
    escalationMatrix: state.grievances.escalationMatrix || [],
    loading: state.grievances.loading,
  }));

  const [filteredMatrix, setFilteredMatrix] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("grievances");

  useEffect(() => {
    dispatch(fetchEscalationMatrix());
  }, [dispatch]);

  useEffect(() => {
    let result = [...escalationMatrix];

    result = result.filter(
      (item) =>
        (typeFilter === "grievances" &&
          item.grant_grievances === "Grievances") ||
        (typeFilter === "grants" && item.grant_grievances === "Grants")
    );

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          (item.at_role && item.at_role.toLowerCase().includes(term)) ||
          (item.escalate_to_role &&
            item.escalate_to_role.toLowerCase().includes(term))
      );
    }

    setFilteredMatrix(result);
  }, [escalationMatrix, searchTerm, typeFilter]);

  if (loading && escalationMatrix.length === 0) {
    return <Loader message="Loading escalation metrics..." />;
  }

  const totalRules = escalationMatrix.length;
  const grievanceRules = escalationMatrix.filter(
    (item) => item.grant_grievances === "Grievances"
  ).length;
  const grantRules = escalationMatrix.filter(
    (item) => item.grant_grievances === "Grants"
  ).length;

  return (
    <div className="container-fluid">
      <DashboardHeader title="Escalation Matrix" />

      {/* Statistics Cards */}
      <div className="stats-grid mb-4">
        <StatCard
          title="Total Escalation Rules"
          value={totalRules}
          icon={Icons.Graph}
          color="#FF5722"
          additionalInfo={
            <>
              <span className="me-2">Grievances: {grievanceRules}</span>
              <span>Grants: {grantRules}</span>
            </>
          }
        />
      </div>

      {/* Filters and Search with modern styling */}
      <ContentCard title="Filters" icon={Icons.Filter}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search escalation metrics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <div className="input-group">
              <span className="input-group-text">
                <i className="bi bi-funnel"></i>
              </span>
              <select
                className="form-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="grievances">Grievances</option>
                <option value="grants">Grants</option>
              </select>
            </div>
          </div>
        </div>
      </ContentCard>

      <ContentCard title="Escalation Matrix" icon={Icons.Graph}>
        <div className="table-responsive">
          <table className="table table-hover border">
            <thead className="table-light">
              <tr>
                <th className="fw-semibold py-3">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-tag-fill me-2 text-primary"></i>
                    Type
                  </div>
                </th>
                <th className="fw-semibold py-3">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-person-badge me-2 text-primary"></i>
                    Current Role
                  </div>
                </th>
                <th className="fw-semibold py-3">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-arrow-up-right-circle me-2 text-primary"></i>
                    Escalation Role
                  </div>
                </th>
                <th className="fw-semibold py-3">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-clock-history me-2 text-primary"></i>
                    Escalation Timeframe
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredMatrix.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-5">
                    <div className="py-4">
                      <i className="bi bi-exclamation-circle fs-1 text-muted mb-3 d-block"></i>
                      <h5 className="text-muted mb-1">
                        No escalation metrics found
                      </h5>
                      <p className="text-muted small">
                        Try adjusting your search criteria
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredMatrix.map((item) => (
                  <tr key={item.matrix_id} className="align-middle">
                    <td>
                      <span
                        className={`badge ${
                          item.grant_grievances === "Grievances"
                            ? "bg-info"
                            : "bg-warning"
                        } text-dark rounded-pill px-3 py-2`}
                      >
                        {item.grant_grievances}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div
                          className="avatar-circle me-2 bg-light d-flex align-items-center justify-content-center"
                          style={{ width: "32px", height: "32px" }}
                        >
                          <i className="bi bi-person text-primary"></i>
                        </div>
                        <span>{item.at_role}</span>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div
                          className="avatar-circle me-2 bg-light d-flex align-items-center justify-content-center"
                          style={{ width: "32px", height: "32px" }}
                        >
                          <i className="bi bi-person-up text-primary"></i>
                        </div>
                        <span>{item.escalate_to_role}</span>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div
                          className="me-2 d-flex align-items-center justify-content-center rounded-circle bg-light"
                          style={{ width: "32px", height: "32px" }}
                        >
                          <i className="bi bi-alarm text-primary"></i>
                        </div>
                        <div>
                          <strong>{item.for_days}</strong> days
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </ContentCard>
    </div>
  );
};

export default EscalationMetrics;
